import { Box, Chip, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import type { AssignmentSummary } from '@/auth/activeAssignment';

import { SuperAdminBadge } from './SuperAdminBadge';

interface Props {
  assignments: AssignmentSummary[];
  activeId?: number | null;
  disabled?: boolean;
  onSelect: (assignment: AssignmentSummary) => void;
}

/**
 * One row per role assignment: its label ("<role> – <organisation>") and
 * applications (Authorization-Plan §6.2 step 2). The server answers no scope
 * or other access internals for the list (identity part 7l).
 */
export const RoleSelectionList = ({
  assignments,
  activeId = null,
  disabled = false,
  onSelect,
}: Props) => (
  <List disablePadding aria-label='Role assignments'>
    {assignments.map((assignment) => {
      const isActive = assignment.id === activeId;
      return (
        <ListItemButton
          key={assignment.id}
          data-testid={`role-assignment-${assignment.id}`}
          selected={isActive}
          disabled={disabled}
          onClick={() => onSelect(assignment)}
          sx={{ borderRadius: 1, mb: 1, border: '1px solid', borderColor: 'divider' }}
        >
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography component='span' sx={{ fontWeight: 600 }}>
                  {assignment.label || `Role ${assignment.id}`}
                </Typography>
                <SuperAdminBadge
                  level={assignment.level}
                  testId={`role-assignment-super-admin-${assignment.id}`}
                />
                {isActive && (
                  <Chip
                    size='small'
                    color='success'
                    icon={<CheckCircleIcon />}
                    label='Current'
                    sx={{ height: 20 }}
                  />
                )}
              </Box>
            }
            secondary={
              assignment.applications && assignment.applications.length > 0 ? (
                <Typography component='span' variant='caption' sx={{ display: 'block' }}>
                  Applications: {assignment.applications.join(', ')}
                </Typography>
              ) : null
            }
          />
        </ListItemButton>
      );
    })}
  </List>
);

export default RoleSelectionList;
